import Storage from 'utils/localStorage/Storage';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import urlUtils from 'utils/Url';
import localeUtils from 'utils/LanguageLocale';

/**
 * Gating helpers for the Customer Service SMS "Text Us" tile.
 *
 * Visibility rule:
 *   show = flagEnabled(locale)
 *          OR (previewEligibleLocale AND (preview param present OR preview marker set))
 *
 *   - Flag ON (any locale) -> tile shown for all users, regardless of param/marker.
 *   - Flag OFF:
 *       * US English / CA English -> the carrier-verification preview override applies:
 *         the tile renders when the page is visited with
 *         `?sms_preview=carrier_verification`, or a marker from a prior preview visit
 *         exists (so a reviewer can navigate away and return without the param).
 *       * CA French -> the param override does NOT apply; visibility follows the flag
 *         only (ECSC-10126 is scoped to US & CA English).
 *
 * Visibility is ALWAYS resolved client-side only so the tile never lands in the
 * server-rendered (SSR/SEO) HTML. The preview param lets SMS carriers verify the
 * publicly visible phone numbers on production. Once A2P approval is granted, the
 * preview param + marker logic can be removed.
 */

const SMS_PREVIEW_PARAM = 'sms_preview';
const SMS_PREVIEW_VALUE = 'carrier_verification';

/**
 * Reads the locale-specific `customerServiceSMS` feature flag.
 * @returns {boolean} true when the flag for the current locale is enabled.
 */
const isSmsTileFlagEnabled = () => {
    const config = Sephora.configurationSettings?.customerServiceSMS;

    if (localeUtils.isFRCanada()) {
        return !!config?.caFrench?.isEnabled;
    }

    if (localeUtils.isCanada()) {
        return !!config?.caEnglish?.isEnabled;
    }

    return !!config?.usEnglish?.isEnabled;
};

/**
 * The carrier-verification preview override is limited to US English and CA English. CA French is excluded.
 * @returns {boolean} true when the current locale is eligible for the param override.
 */
const isPreviewEligibleLocale = () => localeUtils.isUS() || (localeUtils.isCanada() && !localeUtils.isFRCanada());

/**
 * @returns {boolean} true when the carrier-verification preview param is present.
 */
const hasSmsPreviewParam = () => urlUtils.getParamValueAsSingleString(SMS_PREVIEW_PARAM) === SMS_PREVIEW_VALUE;

/**
 * @returns {boolean} true when a prior preview visit left a local storage marker.
 */
const hasSmsPreviewMarker = () => Storage.local.getItem(LOCAL_STORAGE.CUSTOMER_SERVICE_SMS_PREVIEW) === true;

/**
 * Persists the preview marker so the tile stays visible on later visits without
 * the param.
 */
const setSmsPreviewMarker = () => Storage.local.setItem(LOCAL_STORAGE.CUSTOMER_SERVICE_SMS_PREVIEW, true);

/**
 * Resolves whether the SMS "Text Us" tile should render. MUST be called
 * client-side only (e.g. inside useEffect); returns false during SSR so the
 * tile is never part of the server-rendered HTML.
 *
 * The locale feature flag always wins. When the flag is off, the carrier-verification
 * preview (param / marker) is honored for US English and CA English only.
 *
 * Side effect: persists the local storage marker on an eligible param visit.
 * @returns {boolean}
 */
const shouldRenderSmsTile = () => {
    if (Sephora.isNodeRender) {
        return false;
    }

    if (isSmsTileFlagEnabled()) {
        return true;
    }

    if (!isPreviewEligibleLocale()) {
        return false;
    }

    if (hasSmsPreviewParam()) {
        setSmsPreviewMarker();

        return true;
    }

    return hasSmsPreviewMarker();
};

export {
    SMS_PREVIEW_PARAM,
    SMS_PREVIEW_VALUE,
    isSmsTileFlagEnabled,
    isPreviewEligibleLocale,
    hasSmsPreviewParam,
    hasSmsPreviewMarker,
    setSmsPreviewMarker,
    shouldRenderSmsTile
};

export default shouldRenderSmsTile;
